import { Form, Formik } from "formik";
import { nanoid } from "nanoid";
import React from "react";
import { Modal } from "react-bootstrap";

import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";
import rightArrow from "../../images/right-arrow.svg";
import useFileImportStore from "../../stores/fileImportStore";
import { toOrdinal } from "../../utils/text";
import useCancelImport from "./hooks/useCancelImport";
import useHeaderConfirmationModal from "./hooks/useHeaderConfirmationModal";

/**
 *
 * @param name {string}
 */
function getShortItemName(name) {
  if (name.length > 5) {
    return `${name.slice(0, 4)}...`;
  }
  return name;
}

function HeadersPreviewTable() {
  const headers = useFileImportStore((s) => s.headers);
  const finishedGood = useFileImportStore((s) => s.parsedFinishedGood);
  const components = useFileImportStore((s) => s.parsedComponents);

  if (!headers || !finishedGood || !components) {
    return (
      <div className="d-flex justify-content-center align-items-center">
        <LoadingSpinner />
      </div>
    );
  }
  return (
    <table style={{ overflow: "hidden", borderSpacing: 0, borderCollapse: "separate" }} className="table table-borderless border border-1 rounded table-rounded-corners">
      <thead>
        <tr>
          {headers.map((header, idx) => (
            <th className={`${idx !== headers.length - 1 ? "border-end" : ""} border-bottom border-bottom-1`} key={`csvHeader${header}`} scope="col">
              {getShortItemName(header)}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        <tr>
          {finishedGood.map((column, idx) => (
            <td className={`${idx !== headers.length - 1 ? "border-end" : ""}`} key={nanoid()}>
              {column.length > 0 ? getShortItemName(column) : "-"}
            </td>
          ))}
        </tr>
        {components.map((componentRow) => (
          <tr key={nanoid()}>
            {componentRow.map((column, idx) => (
              <td className={`${idx !== headers.length - 1 ? "border-end" : ""}`} key={nanoid()}>
                {column.length > 0 ? getShortItemName(column) : "-"}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function HeaderConfirmationModal() {
  const currentIndex = useFileImportStore((s) => s.currentIndex);
  const unparsedBOMs = useFileImportStore((s) => s.unparsedBOMs);
  const { cancel } = useCancelImport();
  const { handleConfirmHeaders: handleSubmit } = useHeaderConfirmationModal();
  const show = useFileImportStore((s) => s.showHeaderConfirmationModal);
  const headerValidation = useFileImportStore((s) => s.headerValidation);

  return (
    <Modal size="xl" centered show={show} onHide={() => cancel()}>
      <Modal.Body>
        <Formik onSubmit={handleSubmit} initialValues={{ isHeaders: "Y" }}>
          {({ isSubmitting }) => (
            <Form>
              {unparsedBOMs && unparsedBOMs.length > 1 && (
                <p className="text-center">
                  Importing the <b>{toOrdinal(currentIndex + 1)}</b> of <b>{unparsedBOMs.length}</b> BOMs.
                </p>
              )}
              <div className="d-flex gap-3">
                <div>
                  <div className="d-flex align-items-start gap-2 pt-1 mb-3">
                    <p style={{ width: "168px" }} className="m-0">
                      Is this the header of your file? (Do not modify or change.)
                    </p>
                    <img style={{ marginTop: "4px" }} src={rightArrow} alt="Right allow" />
                  </div>
                </div>
                <div>
                  <div className="mb-5">
                    <HeadersPreviewTable />
                  </div>
                  {headerValidation && headerValidation.done && !headerValidation.isValid && (
                    <p className="alert alert-danger mb-5 text-center">Your file doesn&apos;t contain the correct headers. Please see the sample spreadsheet.</p>
                  )}
                  <div className="d-flex justify-content-center align-items-center gap-2">
                    <button type="button" onClick={() => cancel()} className="btn btn-outline-primary text-uppercase fw-bold">
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary text-uppercase fw-bold" disabled={isSubmitting}>
                      Continue
                    </button>
                  </div>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </Modal.Body>
    </Modal>
  );
}

export default HeaderConfirmationModal;
